import React, { useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { RouteComponentProps } from 'react-router-dom';
import { RouteDefinitions } from 'routes';
import { useAuth } from 'shared/providers/AuthProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import DefaultPublic from 'shared/components/Layouts/DefaultPublic';
import { VStack, Box, Heading, Progress } from '@televet/kibble-ui';

const TokenLogin = ({ history, match }: RouteComponentProps<{ token: string }>): JSX.Element => {
  const { token } = match.params;
  const { loginWithToken, isAuthenticated } = useAuth();
  const { clinicUser } = useClinicUser();
  const params = new URLSearchParams(location.search);
  const isImpersonatingParam = params.get('isImpersonating');
  const redirectParam = params.get('redirect');
  const isIntroDisabled = params.get('isIntroDisabled');

  if (isIntroDisabled === 'true') {
    localStorage.setItem('isIntroDisabled', 'true');
  }

  const redirectUser = useCallback(() => {
    if (isAuthenticated && clinicUser) {
      history.push(`${RouteDefinitions.Index}${redirectParam ? `${redirectParam.replace('/', '')}` : ''}`);
    }
  }, [isAuthenticated, clinicUser, history, redirectParam]);

  const loginUser = useCallback(
    async (token: string): Promise<void> => {
      try {
        await loginWithToken(token, Boolean(isImpersonatingParam));
        redirectUser();
      } catch (error) {
        Sentry.captureException(error);
        window.location.href = RouteDefinitions.Logout;
      }
    },
    [loginWithToken, redirectUser, isImpersonatingParam],
  );

  useEffect(() => {
    if (token) {
      loginUser(token);
    }
  }, [loginUser, token]);

  return (
    <DefaultPublic>
      <VStack spacing={4}>
        <Box width="320px">
          <Heading size="md">Loading...</Heading>
          <Progress mt="25px" size="xs" colorScheme="primary" isIndeterminate />
        </Box>
      </VStack>
    </DefaultPublic>
  );
};

export default TokenLogin;
